import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface PortalLayoutProps {
  children: React.ReactNode;
}

export default function PortalLayout({ children }: PortalLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Get user info from session
  const getUserInfo = () => {
    const session = localStorage.getItem('fmr_session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return sessionData.user;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const user = getUserInfo();
  const userName = user?.name || 'Staff User';
  const userRole = user?.role || 'Staff';

  const handleLogout = () => {
    localStorage.removeItem('fmr_session');
    localStorage.removeItem('fmr_remembered_email');
    navigate('/fmr-portal/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/fmr-portal/dashboard', icon: 'ri-dashboard-line' },
    { name: 'Transactions', path: '/fmr-portal/transactions', icon: 'ri-exchange-dollar-line' },
    { name: 'Equipment', path: '/fmr-portal/equipment', icon: 'ri-tools-line' },
    { name: 'Staff', path: '/fmr-portal/staff', icon: 'ri-team-line' },
    { name: 'Bookings', path: '/fmr-portal/bookings', icon: 'ri-calendar-check-line' },
    { name: 'Bank Accounts', path: '/fmr-portal/bank-accounts', icon: 'ri-bank-line' },
    { name: 'Reports', path: '/fmr-portal/reports', icon: 'ri-file-chart-line' },
    { name: 'Receipts', path: '/fmr-portal/receipts', icon: 'ri-receipt-line' },
  ];

  const getPageTitle = () => {
    const item = navItems.find(item => item.path === location.pathname);
    return item?.label || 'Portal';
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col bg-slate-800 border-r border-slate-700 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg">
                  <i className="ri-bar-chart-box-line text-xl text-white"></i>
                </div>
                <span className="text-white font-bold text-lg whitespace-nowrap">FMR Portal</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-slate-400 hover:text-white transition-colors"
                title="Collapse sidebar"
              >
                <i className="ri-menu-fold-line text-xl"></i>
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-full flex justify-center text-slate-400 hover:text-white transition-colors"
              title="Expand sidebar"
            >
              <i className="ri-menu-unfold-line text-xl"></i>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                } ${!sidebarOpen && 'justify-center'}`}
                title={item.label}
              >
                <i className={`${item.icon} text-xl`}></i>
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-700 space-y-3">
          {sidebarOpen && (
            <div className="px-3 py-2 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-user-line text-amber-400 text-sm"></i>
                <p className="text-white text-sm font-medium truncate">{userName}</p>
              </div>
              <p className="text-slate-400 text-xs truncate">{userRole}</p>
            </div>
          )}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-red-400 transition-all whitespace-nowrap ${!sidebarOpen && 'justify-center'}`}
            title="Logout"
          >
            <i className="ri-logout-box-line text-xl"></i>
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-slate-400 hover:text-white transition-colors flex-shrink-0"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
            <h1 className="text-xl font-bold text-white truncate">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-slate-700/50 rounded-lg">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{userName.charAt(0)}</span>
              </div>
              <div className="min-w-0">
                <p className="text-slate-200 text-sm font-medium truncate">{userName}</p>
                <p className="text-slate-400 text-xs truncate">{userRole}</p>
              </div>
            </div>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="text-slate-400 hover:text-red-400 transition-colors whitespace-nowrap flex-shrink-0"
              title="Logout"
            >
              <i className="ri-logout-box-line text-xl"></i>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 flex items-center justify-around px-4 py-3 z-50 safe-area-inset-bottom">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                isActive ? 'text-amber-500' : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              <i className={`${item.icon} text-2xl`}></i>
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-line text-2xl text-red-400"></i>
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">Confirm Logout</h3>
            <p className="text-slate-400 text-center text-sm mb-6">
              Are you sure you want to sign out of the FMR Portal?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}